import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import { DateTime } from 'luxon'
import { useQuery } from '@apollo/client'
import Box from '@material-ui/core/Box'
import LinearProgress from '@material-ui/core/LinearProgress'
import Typography from '@material-ui/core/Typography'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useTheme } from '@material-ui/core/styles'
import { gtmEvent } from 'lib/gtm'
import { onReschedule, onPayment, onCancellation } from 'lib/utils'
import { RefetchContext } from 'lib/utils/refetchContext'
import { flags, useFeatureFlags } from 'lib/feature-flags'
import BOOKINGS_QUERY from '../bookings-query'
import { bookingsInDateRange } from '../bookings-list-helpers'
import CalendarToolbar from './calendar-toolbar'
import CalendarTimeGrid from './calendar-time-grid'
import CalendarMonthView from './calendar-month-view'
import CalendarListView from './calendar-list-view'
import CalendarMobileAgendaView from './calendar-mobile-agenda-view'
import {
  CALENDAR_VIEWS,
  computeVisibleHourRange,
  DESKTOP_CALENDAR_MIN_WIDTH,
  getCalendarDateRange,
  getCalendarRangeLabel,
  getMobileCalendarView,
  getWeekDays,
  groupBookingsByDate,
} from './calendar-utils'
import useCalendarStyles from './calendar-styles'

const SCHEDULE_ORDER_BY = [{ field: 'START_DATE_TIME', direction: 'ASC' }]

const ScheduleCalendar = ({
  onMetadataChange,
  canBookStudent = false,
  bookingUrl,
}) => {
  const classes = useCalendarStyles()
  const theme = useTheme()
  const calendarCardRef = useRef(null)
  const [calendarWidth, setCalendarWidth] = useState(null)
  const isMobileViewport = useMediaQuery(theme.breakpoints.down('sm'))
  const isMobile =
    calendarWidth != null && calendarWidth > 0
      ? calendarWidth < DESKTOP_CALENDAR_MIN_WIDTH
      : isMobileViewport
  const [view, setView] = useState(CALENDAR_VIEWS.WEEK)
  const mobileView = getMobileCalendarView(view)
  const [anchorDate, setAnchorDate] = useState(() => DateTime.local())
  const [selectedFacility, setSelectedFacility] = useState('all')

  const { start, end } = useMemo(() => {
    if (isMobile) {
      return getCalendarDateRange(mobileView, anchorDate)
    }

    return getCalendarDateRange(view, anchorDate)
  }, [isMobile, mobileView, view, anchorDate])

  const [isCoachNotesEnabled] = useFeatureFlags([flags.FLAG_FEAT_COACH_NOTES])

  // Pad the GraphQL window by a day so facility-timezone midnight events near
  // week/day boundaries are still returned when the browser zone differs (e.g.
  // Denver local week start vs Chicago 12:00 AM Sunday). Client filtering uses
  // the booking timezone for the visible range.
  const queryVariables = useMemo(
    () => ({
      startDateTime: {
        from: start.minus({ days: 1 }).toISO(),
        to: end.plus({ days: 1 }).toISO(),
      },
      orderBy: SCHEDULE_ORDER_BY,
      ...(isCoachNotesEnabled ? { includeCoachNotes: true } : {}),
    }),
    [start, end, isCoachNotesEnabled],
  )

  const { data, loading, error, refetch } = useQuery(BOOKINGS_QUERY, {
    variables: queryVariables,
  })

  const schedule = useMemo(() => data?.currentUser?.schedule || [], [
    data?.currentUser?.schedule,
  ])
  const filteredSchedule = useMemo(() => {
    const visibleBookings = schedule.filter(booking => !booking.isCanceled)

    if (
      selectedFacility === 'all' ||
      selectedFacility?.id === 'all' ||
      !selectedFacility
    ) {
      return visibleBookings
    }

    const facilityId =
      typeof selectedFacility === 'string'
        ? selectedFacility
        : selectedFacility.id

    return visibleBookings.filter(
      booking => booking.coachFacility?.id === facilityId,
    )
  }, [schedule, selectedFacility])

  const bookings = useMemo(
    () => bookingsInDateRange(filteredSchedule, start, end),
    [filteredSchedule, start, end],
  )

  const bookingsByDate = useMemo(() => groupBookingsByDate(bookings), [
    bookings,
  ])

  const coachFacilities = useMemo(() => {
    const accountFacilities =
      data?.currentUser?.attributes?.facilities?.filter(
        facility => facility?.id && facility?.name,
      ) || []

    if (accountFacilities.length > 0) {
      return accountFacilities
    }

    // Fallback: facilities referenced by bookings in the current range.
    const facilities = schedule
      .map(booking => booking.coachFacility)
      .filter(facility => facility?.id && facility?.name)

    return facilities.filter(
      (facility, index, all) =>
        all.findIndex(item => item.id === facility.id) === index,
    )
  }, [data?.currentUser?.attributes?.facilities, schedule])

  const hasOneLocation = coachFacilities.length <= 1
  const { startHour, endHour } = useMemo(
    () => computeVisibleHourRange(bookings),
    [bookings],
  )

  const weekDays = useMemo(() => getWeekDays(anchorDate), [anchorDate])

  const rangeLabel = useMemo(() => {
    if (isMobile) {
      return mobileView === CALENDAR_VIEWS.LIST
        ? getCalendarRangeLabel(CALENDAR_VIEWS.LIST, anchorDate)
        : anchorDate.toFormat('LLLL yyyy')
    }

    return getCalendarRangeLabel(view, anchorDate)
  }, [isMobile, mobileView, view, anchorDate])

  useEffect(() => {
    if (onMetadataChange) {
      onMetadataChange({ rangeLabel })
    }
  }, [onMetadataChange, rangeLabel])

  useEffect(() => {
    const node = calendarCardRef.current
    if (!node) {
      return undefined
    }

    const updateWidth = () => {
      setCalendarWidth(node.getBoundingClientRect().width)
    }

    updateWidth()

    if (typeof ResizeObserver === 'undefined') {
      return undefined
    }

    const observer = new ResizeObserver(updateWidth)
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  const handleBookSlotClick = useCallback(() => {
    gtmEvent({
      event: 'formSubmit',
      formCategory: 'book-session',
      formAction: 'clicked-book-session-button',
    })
  }, [])

  const handleReschedule = useCallback(() => onReschedule(refetch), [refetch])
  const handlePayment = useCallback(() => onPayment(refetch), [refetch])
  const handleCancellation = useCallback(() => onCancellation(refetch), [
    refetch,
  ])

  const sessionRowHandlers = {
    onReschedule: handleReschedule,
    onPayment: handlePayment,
    onCancellation: handleCancellation,
  }

  const renderView = () => {
    if (loading) {
      return <LinearProgress color="primary" />
    }

    if (error) {
      return (
        <Box p={3}>
          <Typography color="error">
            We couldn&apos;t load your schedule. Please refresh and try again.
          </Typography>
        </Box>
      )
    }

    if (isMobile) {
      if (mobileView === CALENDAR_VIEWS.LIST) {
        return (
          <CalendarListView
            days={weekDays}
            bookingsByDate={bookingsByDate}
            {...sessionRowHandlers}
          />
        )
      }

      return (
        <CalendarMobileAgendaView
          anchorDate={anchorDate}
          onDateChange={setAnchorDate}
          bookingsByDate={bookingsByDate}
          {...sessionRowHandlers}
        />
      )
    }

    if (view === CALENDAR_VIEWS.DAY) {
      return (
        <CalendarTimeGrid
          days={[anchorDate]}
          bookings={bookings}
          startHour={startHour}
          endHour={endHour}
          title={anchorDate.toFormat('cccc, LLL d')}
          canBookStudent={canBookStudent}
          bookingUrl={bookingUrl}
          onBookSlotClick={handleBookSlotClick}
          {...sessionRowHandlers}
        />
      )
    }

    if (view === CALENDAR_VIEWS.WEEK) {
      return (
        <CalendarTimeGrid
          days={weekDays}
          bookings={bookings}
          startHour={startHour}
          endHour={endHour}
          canBookStudent={canBookStudent}
          bookingUrl={bookingUrl}
          onBookSlotClick={handleBookSlotClick}
          {...sessionRowHandlers}
        />
      )
    }

    if (view === CALENDAR_VIEWS.MONTH) {
      return (
        <CalendarMonthView
          anchorDate={anchorDate}
          bookingsByDate={bookingsByDate}
          canBookStudent={canBookStudent}
          bookingUrl={bookingUrl}
          onBookSlotClick={handleBookSlotClick}
          {...sessionRowHandlers}
        />
      )
    }

    return (
      <CalendarListView
        days={weekDays}
        bookingsByDate={bookingsByDate}
        {...sessionRowHandlers}
      />
    )
  }

  return (
    <RefetchContext.Provider value={refetch}>
      <Box className={classes.root}>
        <Box className={classes.calendarCard} ref={calendarCardRef}>
          <CalendarToolbar
            view={view}
            anchorDate={anchorDate}
            isMobile={isMobile}
            onViewChange={setView}
            onToday={() => setAnchorDate(DateTime.local())}
            onDateChange={setAnchorDate}
            selectedFacility={selectedFacility}
            onSelectedFacilityChange={setSelectedFacility}
            coachFacilities={coachFacilities}
            hasOneLocation={hasOneLocation}
          />

          {renderView()}
        </Box>
      </Box>
    </RefetchContext.Provider>
  )
}

ScheduleCalendar.propTypes = {
  onMetadataChange: PropTypes.func,
  canBookStudent: PropTypes.bool,
  bookingUrl: PropTypes.string,
}

export default ScheduleCalendar
